import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Minus, RotateCcw, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/components/language-provider";
import { useIsMobile } from "@/hooks/use-mobile";
import { getApiUrl } from "@/lib/config";
import { Mascot, MascotMood, mascotFrameUrl, preloadMascot } from "./mascot";
import "./assistant.css";

const UI_KEY = "fs:site-assistant:ui";
const CONV_KEY = "fs:site-assistant:conv";
const SLOW_AFTER_MS = 7000;
const GREETING = /^\s*(oi+|ol[aá]|e a[ií]|bom dia|boa tarde|boa noite|hi+|hello|hey|yo)\b/i;

type UiState = "closed" | "open" | "min";

interface Line {
  key: string;
  role: "user" | "assistant";
  text: string;
  outcome?: string;
}

interface Turn {
  conversationId: string;
  outcome: string;
  reply: { text: string; mood?: string | null };
}

const TEXTS = {
  pt: {
    name: "Chat FillSign",
    tagline: "Tire dúvidas sobre o FillSign",
    open: "Dúvidas? Fale pelo chat",
    close: "Fechar",
    minimize: "Minimizar",
    newChat: "Nova conversa",
    placeholder: "Pergunte sobre planos ou como funciona...",
    send: "Enviar",
    greet: "Olá!",
    hello: "Olá! Este é o chat do FillSign. Posso explicar planos e preços, como a assinatura funciona, a integração com o Dynamics 365 ou registrar seu contato para a equipe.",
    suggestions: ["Quanto custa?", "A assinatura tem validade jurídica?", "Como funciona com o Dynamics 365?", "Quero falar com vendas"],
    thinking: "Consultando...",
    slow: "Só mais um instante...",
    typingAlong: "Pode escrever.",
    offline: "Não consegui falar com o servidor agora. Tente de novo em instantes.",
    tooMany: "Muitas mensagens em pouco tempo. Tente de novo daqui a um minuto.",
    poke: ["Olá!", "Em que posso ajudar?", "Pronto para ajudar."],
  },
  en: {
    name: "FillSign chat",
    tagline: "Questions about FillSign",
    open: "Questions? Use the chat",
    close: "Close",
    minimize: "Minimize",
    newChat: "New conversation",
    placeholder: "Ask about plans or how it works...",
    send: "Send",
    greet: "Hello!",
    hello: "Hello! This is the FillSign chat. I can explain plans and pricing, how signing works, the Dynamics 365 integration, or pass your contact on to the team.",
    suggestions: ["How much does it cost?", "Are signatures legally valid?", "How does it work with Dynamics 365?", "I want to talk to sales"],
    thinking: "Checking...",
    slow: "Just a moment...",
    typingAlong: "Go ahead.",
    offline: "I couldn't reach the server right now. Please try again in a moment.",
    tooMany: "Too many messages in a short time. Try again in a minute.",
    poke: ["Hello!", "How can I help?", "Ready to help."],
  },
};

/** Floating chat of the website: plans, FAQ and sales contact, answered by the public assistant endpoint. */
export function SiteAssistantWidget() {
  const { language } = useLanguage();
  const t = useMemo(() => TEXTS[language === "en" ? "en" : "pt"], [language]);
  const isMobile = useIsMobile();

  const [ui, setUi] = useState<UiState>(() => (sessionStorage.getItem(UI_KEY) as UiState) || "closed");
  const [conversationId, setConversationId] = useState<string | null>(() => sessionStorage.getItem(CONV_KEY));
  const [lines, setLines] = useState<Line[]>([]);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [slow, setSlow] = useState(false);
  const [mood, setMood] = useState<MascotMood>("idle");
  const [speech, setSpeech] = useState<string | null>(null);

  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const moodTimer = useRef<number | undefined>(undefined);
  const speechTimer = useRef<number | undefined>(undefined);
  const typingTimer = useRef<number | undefined>(undefined);
  const pendingRef = useRef(false);

  const say = useCallback((text: string | null, ms = 2600) => {
    window.clearTimeout(speechTimer.current);
    setSpeech(text);
    if (text) speechTimer.current = window.setTimeout(() => setSpeech(null), ms);
  }, []);

  const feel = useCallback((m: MascotMood, ms?: number) => {
    window.clearTimeout(moodTimer.current);
    setMood(m);
    if (ms) moodTimer.current = window.setTimeout(() => setMood(pendingRef.current ? "thinking" : "idle"), ms);
  }, []);

  useEffect(() => { sessionStorage.setItem(UI_KEY, ui); }, [ui]);
  useEffect(() => {
    if (conversationId) sessionStorage.setItem(CONV_KEY, conversationId);
    else sessionStorage.removeItem(CONV_KEY);
  }, [conversationId]);

  useEffect(() => {
    if (ui !== "open") return;
    preloadMascot();
    feel("wave", 2200);
    say(t.greet, 1800);
    const focus = window.setTimeout(() => inputRef.current?.focus(), 250);
    return () => window.clearTimeout(focus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines, pending, ui]);

  useEffect(() => () => {
    window.clearTimeout(moodTimer.current);
    window.clearTimeout(speechTimer.current);
    window.clearTimeout(typingTimer.current);
  }, []);

  const onType = (value: string) => {
    setInput(value);
    if (pending) return;
    if (value.trim()) { if (mood !== "writing") { setMood("writing"); say(t.typingAlong, 1400); } }
    else setMood("idle");
    window.clearTimeout(typingTimer.current);
    typingTimer.current = window.setTimeout(() => { if (!pendingRef.current) setMood("idle"); }, 1500);
  };

  const send = async (text: string) => {
    const message = text.trim();
    if (!message || pending) return;
    setInput("");
    window.clearTimeout(typingTimer.current);
    setLines((l) => [...l, { key: `u${Date.now()}`, role: "user", text: message }]);
    setPending(true);
    pendingRef.current = true;
    setSlow(false);
    if (GREETING.test(message)) { feel("wave"); window.setTimeout(() => { if (pendingRef.current) setMood("thinking"); }, 1200); }
    else { feel("thinking"); say(t.thinking, 1600); }
    const slowTimer = window.setTimeout(() => { setSlow(true); feel("sorry"); say(t.slow, 3500); }, SLOW_AFTER_MS);
    try {
      const res = await fetch(getApiUrl("/api/assistant/public"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, conversationId, lang: language }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const turn: Turn = await res.json();
      setConversationId(turn.conversationId);
      setLines((l) => [...l, { key: `a${Date.now()}`, role: "assistant", text: turn.reply.text, outcome: turn.outcome }]);
      feel(replyMood(turn.reply.mood, turn.outcome), 4500);
    } catch (err) {
      const status = Number((err as Error).message);
      setLines((l) => [...l, { key: `e${Date.now()}`, role: "assistant", text: status === 429 ? t.tooMany : t.offline, outcome: "error" }]);
      feel("sad", 4500);
    } finally {
      window.clearTimeout(slowTimer);
      setPending(false);
      pendingRef.current = false;
      setSlow(false);
      window.setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const newConversation = () => {
    setConversationId(null);
    setLines([]);
    feel("wave", 1800);
    inputRef.current?.focus();
  };

  const poke = () => {
    const moods: MascotMood[] = ["surprised", "happy", "wave"];
    feel(moods[Math.floor(Math.random() * moods.length)], 1600);
    say(t.poke[Math.floor(Math.random() * t.poke.length)], 1600);
  };

  if (ui !== "open") {
    return (
      <button
        type="button"
        onClick={() => setUi("open")}
        className="fs-assistant-launcher fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-full border bg-card pl-1 pr-3 py-1 shadow-lg hover:shadow-xl transition-shadow"
        style={{ borderColor: "var(--fs-sage, #9bc27a)" }}
        aria-label={t.open}
        data-testid="site-assistant-launcher"
      >
        <img src={mascotFrameUrl("writing")} alt="" className="h-11 w-11" draggable={false} />
        <span className="text-sm font-medium hidden sm:inline">{ui === "min" && lines.length > 0 ? t.name : t.open}</span>
      </button>
    );
  }

  const panelClass = isMobile
    ? "fixed inset-0 z-50 flex flex-col bg-card"
    : "fs-assistant-panel fixed bottom-4 right-4 z-50 flex w-[380px] max-w-[calc(100vw-2rem)] flex-col rounded-2xl border bg-card shadow-2xl";

  return (
    <div className={panelClass} style={isMobile ? undefined : { height: "min(560px, calc(100vh - 2rem))" }} role="dialog" aria-label={t.name} data-testid="site-assistant-panel">
      <header className="flex items-center gap-3 border-b px-3 py-2">
        <Mascot mood={mood} size={52} onClick={poke} title={t.name} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight">{t.name}</p>
          {speech ? (
            <p className="fs-assistant-speech inline-block max-w-full truncate rounded-xl rounded-tl-sm px-2 py-0.5 text-xs" style={{ background: "var(--fs-forest, #2f5d3a)", color: "#fff" }}
              data-testid="site-assistant-speech">
              {speech}
            </p>
          ) : (
            <p className="truncate text-xs text-muted-foreground">{pending ? (slow ? t.slow : t.thinking) : t.tagline}</p>
          )}
        </div>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={newConversation} aria-label={t.newChat} title={t.newChat}>
          <RotateCcw className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setUi("min")} aria-label={t.minimize} title={t.minimize}>
          <Minus className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setUi("closed")} aria-label={t.close} title={t.close}>
          <X className="h-4 w-4" />
        </Button>
      </header>

      <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-3 py-3" data-testid="site-assistant-messages">
        {lines.length === 0 && (
          <>
            <Bubble role="assistant" text={t.hello} />
            <div className="flex flex-wrap gap-2 pl-9">
              {t.suggestions.map((s) => (
                <button key={s} type="button" onClick={() => send(s)} className="rounded-full border bg-background px-3 py-1 text-xs hover:bg-muted transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </>
        )}
        {lines.map((m) => <Bubble key={m.key} role={m.role} text={m.text} outcome={m.outcome} />)}
        {pending && (
          <div className="flex items-end gap-2">
            <Mascot mood="thinking" size={28} />
            <div className="fs-assistant-typing rounded-2xl rounded-bl-sm bg-muted px-3 py-2 text-foreground"><span /><span /><span /></div>
          </div>
        )}
      </div>

      <form className="flex items-end gap-2 border-t p-2" onSubmit={(e) => { e.preventDefault(); void send(input); }}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => onType(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(input); } }}
          rows={1}
          maxLength={2000}
          placeholder={t.placeholder}
          className="max-h-28 min-h-[40px] flex-1 resize-none rounded-xl border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
          data-testid="site-assistant-input"
        />
        <Button type="submit" size="icon" className="h-10 w-10 shrink-0 rounded-xl" disabled={pending || !input.trim()} aria-label={t.send} data-testid="site-assistant-send">
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
    </div>
  );
}

function replyMood(mood: string | null | undefined, outcome: string): MascotMood {
  if (outcome === "limit") return "sleepy";
  if (outcome === "unavailable" || outcome === "disabled") return "sad";
  if (outcome === "offtopic") return "neutral";
  switch (mood) {
    case "happy": case "sorry": case "sad": case "sleepy": case "thinking": case "wave": case "surprised":
      return mood;
    default:
      return "idle";
  }
}

function Bubble({ role, text, outcome }: { role: "user" | "assistant"; text: string; outcome?: string }) {
  if (role === "user") {
    return (
      <div className="fs-assistant-bubble flex justify-end">
        <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-sm px-3 py-2 text-sm text-white" style={{ background: "var(--fs-forest, #2f5d3a)" }}>{text}</div>
      </div>
    );
  }
  const tone = outcome === "error" || outcome === "unavailable" ? "border-destructive/40" : "";
  return (
    <div className="fs-assistant-bubble flex items-end gap-2">
      <img src={mascotFrameUrl("idle_a")} alt="" className="h-7 w-7 shrink-0" draggable={false} />
      <div className={`max-w-[85%] rounded-2xl rounded-bl-sm border bg-muted px-3 py-2 text-sm ${tone}`}>
        <FormattedText text={text} />
      </div>
    </div>
  );
}

function FormattedText({ text }: { text: string }) {
  const blocks = useMemo(() => {
    const out: { type: "p" | "ul"; lines: string[] }[] = [];
    for (const raw of text.split(/\r?\n/)) {
      const line = raw.trimEnd();
      const bullet = /^\s*(?:[-•*]|\d+[.)])\s+(.*)$/.exec(line);
      if (bullet) {
        if (out[out.length - 1]?.type === "ul") out[out.length - 1].lines.push(bullet[1]);
        else out.push({ type: "ul", lines: [bullet[1]] });
      } else if (line.trim()) {
        out.push({ type: "p", lines: [line] });
      }
    }
    return out;
  }, [text]);
  return (
    <div className="space-y-1.5">
      {blocks.map((b, i) => b.type === "ul" ? (
        <ul key={i} className="list-disc space-y-0.5 pl-4">{b.lines.map((l, j) => <li key={j}><Inline text={l} /></li>)}</ul>
      ) : (
        <p key={i} className="break-words"><Inline text={b.lines[0]} /></p>
      ))}
    </div>
  );
}

function Inline({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|https?:\/\/[^\s)]+)/g);
  return (
    <>
      {parts.map((p, i) => {
        if (!p) return null;
        if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
        if (/^\*[^*\s][^*]*\*$/.test(p)) return <strong key={i}>{p.slice(1, -1)}</strong>;
        if (/^https?:\/\//.test(p)) return <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="underline break-all">{p}</a>;
        return <span key={i}>{p}</span>;
      })}
    </>
  );
}
